/**
 * Theme MUI — palette gần với thương hiệu Tuan Loc Commodities (trang công khai
 * https://tlcommodities.com/ ): xanh rừng, nền kem ấm, nhấn vàng đồng.
 */
import { createTheme, alpha } from '@mui/material/styles'

const TLC = {
  primaryMain: '#0c4744',
  primaryLight: '#1a7f5a',
  primaryDark: '#062523',
  secondaryMain: '#c9a961',
  secondaryLight: '#fbeed1',
  secondaryDark: '#a8893f',
  bgDefault: '#f8f9fb',
  bgSoft: '#fafbfc',
  paper: '#ffffff',
  ink: '#0f1720',
  muted: '#5b6770',
} as const

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: TLC.primaryMain,
      light: TLC.primaryLight,
      dark: TLC.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: TLC.secondaryMain,
      light: TLC.secondaryLight,
      dark: TLC.secondaryDark,
      contrastText: '#1a1f1c',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#5c7c72',
      light: '#7d9a91',
      dark: '#3f554d',
      contrastText: '#ffffff',
    },
    background: {
      default: TLC.bgDefault,
      paper: TLC.paper,
    },
    text: {
      primary: TLC.ink,
      secondary: TLC.muted,
      disabled: '#94a3b8',
    },
    divider: alpha(TLC.ink, 0.1),
  },
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.9375rem',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.8125rem',
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: '#5c665f',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.06)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--app-radius-md': '12px',
          '--app-radius-lg': '16px',
          '--app-sidebar-icon': 'rgba(255,255,255,0.82)',
          '--app-sidebar-active-icon': TLC.secondaryLight,
          '--app-sidebar-active-border': TLC.secondaryMain,
          '--app-sidebar-active-bg-solid': '#e8f1ee',
          '--app-table-head-bg': '#f1f3f5',
          '--app-table-head-text': '#0f1720',
          '--app-table-row-alt': 'rgba(15, 23, 32, 0.04)',
          '--app-table-row-hover': 'rgba(9, 54, 51, 0.08)',
          '--app-table-border': '#e3e6ea',
          '--n2nai-primary-main': TLC.primaryMain,
          '--n2nai-primary-dark': TLC.primaryDark,
          '--n2nai-secondary-main': TLC.secondaryMain,
          '--n2nai-secondary-dark': TLC.secondaryDark,
          '--n2nai-bg-default': TLC.bgDefault,
        },
        '.theme-light': {
          '--app-bg': TLC.bgDefault,
          '--app-sidebar-bg': '#fafbfc',
          '--app-sidebar-active-bg': 'linear-gradient(90deg, #e3f1ee, rgba(201,169,97,0.16))',
          '--app-sidebar-text': TLC.primaryDark,
          '--app-sidebar-muted': '#8a939d',
          '--app-sidebar-icon': '#5b6770',
          '--app-sidebar-active-icon': TLC.secondaryDark,
          '--app-sidebar-hover-bg': 'rgba(15, 23, 32, 0.04)',
          '--app-sidebar-active-text': TLC.primaryDark,
          '--app-topbar-bg': TLC.primaryDark,
          '--app-topbar-text': '#f4f0e4',
          '--app-card-bg': TLC.paper,
          '--app-border-subtle': '#e3e6ea',
          '--app-shadow-soft': '0 1px 3px rgba(15, 23, 32, 0.10)',
        },
        '.theme-dark': {
          '--app-bg': '#0f1614',
          '--app-sidebar-bg': '#0f241e',
          '--app-sidebar-active-bg': TLC.primaryDark,
          '--app-sidebar-text': '#f9fafb',
          '--app-sidebar-muted': '#9ca3af',
          '--app-topbar-bg': '#0f241e',
          '--app-card-bg': '#0f1614',
          '--app-border-subtle': 'rgba(15, 23, 42, 0.7)',
          '--app-shadow-soft': '0 1px 4px rgba(15, 23, 42, 0.85)',
        },
        html: { height: '100%', width: '100%' },
        body: {
          height: '100%',
          width: '100%',
          margin: 0,
          backgroundColor: 'var(--app-bg)',
          display: 'block',
        },
        '#root': { height: '100%', width: '100%', display: 'block' },
        '.app-shell': {
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--app-bg)',
        },
        '.MuiAppBar-root': {
          backgroundImage:
            'linear-gradient(90deg, #062523 0%, #093633 46%, #0c4744 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 1px 8px rgba(15, 23, 32, 0.18)',
        },
        '.page-header': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        },
        '.page-header-title': {
          fontSize: '1.125rem',
          fontWeight: 600,
        },
        '.page-header-subtitle': {
          fontSize: '0.875rem',
          color: '#6b7280',
        },
        '.page-card': {
          backgroundColor: 'var(--app-card-bg)',
          borderRadius: 'var(--app-radius-lg)',
          boxShadow: 'var(--app-shadow-soft)',
          border: `1px solid var(--app-border-subtle)`,
        },
        '.page-table-card': {
          backgroundColor: 'var(--app-card-bg)',
          borderRadius: 'var(--app-radius-lg)',
          boxShadow: 'var(--app-shadow-soft)',
          border: `1px solid var(--app-border-subtle)`,
          overflow: 'hidden',
        },
        '.status-chip-warning': {
          backgroundColor: 'rgba(245, 158, 11, 0.12)',
          color: '#b45309',
          borderRadius: '999px',
          fontSize: '0.75rem',
          padding: '4px 10px',
          fontWeight: 600,
        },
        '.status-chip-primary': {
          backgroundColor: alpha(TLC.primaryMain, 0.1),
          color: TLC.primaryMain,
          borderRadius: '999px',
          fontSize: '0.75rem',
          padding: '4px 10px',
          fontWeight: 600,
        },
        '.btn-primary-rounded': {
          borderRadius: '999px',
          paddingInline: '18px',
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          fontWeight: 700,
          letterSpacing: '0.01em',
          padding: '7px 18px',
          boxShadow: 'none',
          transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(TLC.primaryMain, 0.18)}`,
            transform: 'translateY(-1px)',
          },
          '&.Mui-disabled': {
            transform: 'none',
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundImage: `linear-gradient(180deg, ${alpha('#ffffff', 0.08)}, ${alpha('#000000', 0.04)})`,
          '&:hover': {
            boxShadow: `0 5px 14px ${alpha(TLC.primaryMain, 0.26)}`,
          },
        },
        outlined: {
          borderWidth: 1,
          backgroundColor: alpha(TLC.paper, 0.68),
          '&:hover': {
            borderWidth: 1,
            backgroundColor: alpha(TLC.primaryMain, 0.055),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(23, 35, 29, 0.08), 0 1px 2px -1px rgba(23, 35, 29, 0.06)',
          border: '1px solid rgba(23, 35, 29, 0.08)',
          '&:hover': {
            boxShadow: '0 10px 20px -10px rgba(23, 35, 29, 0.2)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(23, 35, 29, 0.08)',
          border: '1px solid rgba(23, 35, 29, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 1px 8px 0 rgba(11, 38, 31, 0.22)',
          borderBottom: `1px solid ${alpha(TLC.secondaryMain, 0.22)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: `1px solid ${alpha(TLC.secondaryMain, 0.18)}`,
          boxShadow: '2px 0 12px rgba(11, 38, 31, 0.18)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: TLC.paper,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: TLC.primaryMain,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: TLC.primaryMain,
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.Mui-selected': {
            background: 'var(--app-sidebar-active-bg)',
            color: '#ffffff',
            '&:hover': {
              background: 'var(--app-sidebar-active-bg)',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: TLC.bgDefault,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
})

export default theme
